import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../layout/Header';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Download, Eye, Filter, Edit2, Trash2, FileCheck } from 'lucide-react';
import { DOCUMENT_TYPES } from '../data/gestionDocumentaireTypes';
import PlanDirecteurModal from '../components/PlanDirecteurModal';
import PlanDirecteurTechniqueModal from '../components/PlanDirecteurTechniqueModal';
import Roadmap24Modal from '../components/Roadmap24Modal';
import { 
  ObjectifsKPIModal, 
  CahierChargeModal, 
  MatriceRACIModal, 
  PlanBudgetaireModal,
  PlanBudgetaire5AnsModal, 
  RegistreAmeliorationModal, 
  RegistreRisquesModal, 
  OffreServiceBZModal,
  PolitiqueCybersecuriteModal,
  RapportIncidentModal,
  PolitiqueIAModal,
  PolitiqueGestionIncidentsModal,
  PolitiqueSecuriteInformationModal,
  PolitiqueClassificationIncidentsModal,
  PolitiqueEscaladeIncidentsModal,
  PlanInterventionIncidentModal,
  PlanIncidentMajeurModal,
  PlanCommunicationIncidentModal,
  PlanContinuiteTiModal,
  PlanIncidentViePriveeModal,
  ProcedureGestionIncidentsModal,
  ProcedureReponseIncidentsSecuriteModal,
  ProcedureEscaladeModal,
  ProcedureGestionPreuvesModal,
  PostIncidentReviewModal,
  AfterActionReportModal,
  RapportLeconsApprisesModal
} from '../components/AllDocumentModals';
import PriseDeBesoinModal from '../components/PriseDeBesoinModal';
import PriseDeBesoinWizardModal from '../components/PriseDeBesoinWizardModal';
import PriseDeBesoinPolitiqueCybersecuriteModal from '../components/PriseDeBesoinPolitiqueCybersecuriteModal';
import PriseDeBesoinPlanDirecteurTechniqueModal from '../components/PriseDeBesoinPlanDirecteurTechniqueModal';
import PriseDeBesoinPlanBudgetaire5AnsModal from '../components/PriseDeBesoinPlanBudgetaire5AnsModal';
import ExportTemplateExcelModal from '../components/ExportTemplateExcelModal';
import BesoinListSection from '../components/BesoinListSection';
import TemplateDocumentLinksModal from '../components/TemplateDocumentLinksModal';
import TemplateLinkedDocumentCell from '../components/TemplateLinkedDocumentCell';
import DocumentCreationWizardModal from '../components/DocumentCreationWizardModal';
import ManagedDocumentViewerModal from '../components/ManagedDocumentViewerModal';
import DocumentEditModal from '../components/DocumentEditModal';
import { getAllDocuments, deleteDocument, getDocumentHistorique, type DocumentRecord } from '../services/documentsService';
import { StarRating } from '../components/StarRating';
import MethodologyFlowchart from '../components/MethodologyFlowchart';
import { getAllClients, type ClientItem } from '../services/clientService';
import { getPartiesPrenantes, getPartiePrenanteAvatarUrl, type PartiePrenante } from '../services/partiesPrenantesService';
import { INTAKE_TEMPLATES } from '../domain/besoins/templates';
import { getAllBesoins } from '../services/besoinsService';

type IntakeTemplateSummary = {
  id: string;
  name: string;
  description: string;
  status: 'production' | 'coming_soon' | 'archived';
};

const INTAKE_TEMPLATES_BY_TYPE: Record<string, IntakeTemplateSummary[]> = {
  PRISME_KMI: [
    {
      id: 'prisme-kmi-v1',
      name: 'Prisme - KMI',
      description:
        'Questionnaire de diagnostic KMI en 6 piliers (échelle 0 à 5 par question, 0 = on ne s\'en occupe pas). Sert à générer le diagnostic maturité actuelle et le spider « Maturité actuelle ».',
      status: 'production',
    },
  ],
  PRISME_KMI_CIBLE: [
    {
      id: 'prisme-kmi-cible-v1',
      name: 'Prisme - KMI (maturité cible Futur)',
      description:
        'Questionnaire KMI en 6 piliers adapté à l’état visé. Utilisé pour définir la maturité cible Futur et alimenter le spider « Maturité cible » dans PRIsME Client.',
      status: 'archived',
    },
  ],
  PLAN_DIRECTEUR: [
    {
      id: 'pdti-standard-pme',
      name: 'Prise de besoin Plan Directeur TI – PME',
      description:
        'Version standard pour PME (20–250 employés): contexte d\'affaires, maturité TI, enjeux, objectifs 3–5 ans, contraintes, priorités.',
      status: 'production',
    },
    {
      id: 'pdti-pme-v2',
      name: 'Prise de besoin Plan Directeur TI – PME V2',
      description:
        'Basé sur les 7 piliers BZ (Stratégie & Gouvernance, Actifs, Projet, Services TI, Cybersécurité, Amélioration continue, Innovation & IA). Inclut une section « Déjà en place » pour éviter des recommandations redondantes. Les documents générés utilisent 7 dimensions (spider/tableaux).',
      status: 'production',
    },
    {
      id: 'pdti-pme-v3',
      name: 'Prise de besoin Plan Directeur TI – PME V3',
      description:
        'Version orientée besoins d’affaires: contexte, objectifs, irritants, risques, processus critiques, systèmes, opérations, sécurité, budget et appétit client. Garde la page Métier et mappe ensuite vers le cadre BZ 6 + 2, les tactiques, les livrables et les 3 offres de service.',
      status: 'production',
    },
    {
      id: 'prise-de-besoin-client-2026',
      name: 'Prise de besoin - Maturité et besoins client',
      description:
        'Formulaire combiné de maturité TI (KMI As-Was, As-Is et To-Be) et de prise de besoins, structuré selon les 6 piliers du cadre BZ.',
      status: 'production',
    },
    {
      id: 'pdti-large-entreprise',
      name: 'Prise de besoin Plan Directeur TI – Grande entreprise',
      description:
        'Version avancée pour organisations complexes: multi-sites, portefeuilles applicatifs, risques, capacité d\'investissement, trajectoire cloud.',
      status: 'coming_soon',
    },
  ],
  PLAN_DIRECTEUR_5_ANS_V2: [
    {
      id: 'prisme-kmi-v1',
      name: 'Prisme - KMI',
      description:
        'Source requise pour le Plan Directeur 5 ans V2: maturité actuelle par pilier, scores, écarts et posture de départ.',
      status: 'archived',
    },
    {
      id: 'prise-de-besoin-client-2026',
      name: 'Prise de besoin - Maturité et besoins client',
      description:
        'Source combinée de maturité et de besoins client pour le Plan Directeur 5 ans V2.',
      status: 'archived',
    },
  ],
  PLAN_DIRECTEUR_TECHNIQUE: [
    {
      id: 'plan-directeur-technique-v1',
      name: 'Prise de besoin Plan Directeur TI - Analyse Technique & Budget',
      description:
        'Questionnaire complet pour analyse technique approfondie : infrastructure, équipements, RTO/RPO, sauvegardes, sécurité, budget détaillé, conformité Loi 25.',
      status: 'production',
    },
  ],
  PRISE_BESOIN_CLIENT_2026: [
    {
      id: 'prise-de-besoin-client-2026',
      name: 'Prise de besoin - Maturité et besoins client',
      description:
        'Formulaire tabulaire basé sur le fichier de référence: 66 questions, barèmes de maturité 0 à 5, réponses, priorités, solutions, coûts, efforts, risques et mitigations.',
      status: 'archived',
    },
  ],
  PRISE_BESOIN_CLIENT_2026_ANALYSE: [
    {
      id: 'prise-de-besoin-client-2026-analyse',
      name: 'Prise de besoin - Maturité et besoins client — Analyse & recommandations (IA)',
      description:
        'Document narratif professionnel généré par IA à partir des réponses du questionnaire. Aucune donnée inventée; les informations manquantes sont indiquées comme « Non précisé ». Inclut une section de bonnes pratiques (génériques) clairement séparée.',
      status: 'production',
    },
  ],
  ODS: [
    {
      id: 'ods-support-gestion',
      name: 'Prise de besoin - ODS',
      description:
        'Focalisée sur le modèle de support, les attentes SLA, les heures d’ouverture, la criticité des systèmes et le modèle d’engagement souhaité.',
      status: 'production',
    },
  ],
  EBAUCHE_ODS: [
    {
      id: 'ods-support-gestion',
      name: 'Prise de besoin - ODS',
      description:
        'Source requise pour l’Ébauche d’ODS: portée de services, modèle de support, SLA, heures d’ouverture, criticité, exclusions, tarification et produits additionnels.',
      status: 'production',
    },
    {
      id: 'prise-de-besoin-client-2026',
      name: 'Prise de besoin - Maturité et besoins client',
      description:
        'Source requise pour l’Ébauche d’ODS: contexte client, besoins, objectifs SMART, priorités, solutions, coûts, risques, contraintes et éléments à valider.',
      status: 'archived',
    },
    {
      id: 'prisme-kmi-v1',
      name: 'Prisme - KMI',
      description:
        'Source requise pour l’Ébauche d’ODS: maturité actuelle par pilier, écarts, posture cible et lecture stratégique pour orienter les trois offres.',
      status: 'archived',
    },
  ],
  PLAN_BUDGETAIRE_TI_N1: [],
  PLAN_BUDGETAIRE_5_ANS: [
    {
      id: 'plan-budgetaire-5ans-v1',
      name: 'Prise de besoin - Plan budgétaire 5 ans',
      description:
        'Formulaire de saisie du plan budgétaire vision 5 ans : montants par poste et par année (infrastructure, logiciels, services TI, postes), avec projets en cours et améliorations faites/suggérées.',
      status: 'production',
    },
  ],
  PLAN_CYBER: [
    {
      id: 'cyber-nist25',
      name: 'Prise de besoin Plan Cyber – NIST 2.0 / Loi 25',
      description:
        'Axée sur les domaines NIST 2.0, la réalité Loi 25 du client, la posture actuelle, les écarts, les données sensibles et les obligations légales.',
      status: 'coming_soon',
    },
    {
      id: 'loi25-efvp',
      name: 'Évaluation des facteurs relatifs à la vie privée (Loi 25)',
      description:
        'Questionnaire structuré pour réaliser une EFVP: traitements, catégories de données, risques pour la vie privée et mesures de mitigation.',
      status: 'coming_soon',
    },
    {
      id: 'cyber-qcm-nist',
      name: 'Questionnaire NIST (maturité cybersécurité)',
      description:
        'Questionnaire de prise de besoin basé sur NIST 2.0 pour évaluer la maturité cybersécurité du client et préparer les rapports de risques.',
      status: 'coming_soon',
    },
  ],
  POLITIQUE_CYBER: [
    {
      id: 'politique-cybersecurite-v2',
      name: 'Prise de besoin Politique de cybersécurité v2',
      description:
        'Questionnaire complet pour élaborer une politique de cybersécurité alignée sur NIST CSF 2.0, ITIL 4, COBIT 2019 et ISO 27001. Couvre : utilisations autorisées, logiciels, médias sociaux, BYOD, contrôles d\'accès, authentification, mots de passe, courriel, restrictions et IA.',
      status: 'production',
    },
  ],
  RAPPORT_INCIDENT: [
    {
      id: 'rapport-incident-v1',
      name: 'Prise de besoin Rapport d\'incident TI v1',
      description:
        'Questionnaire complet pour générer un rapport d\'incident TI conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Documente la chronologie, l\'analyse de cause racine, les impacts, les mesures de réponse (confinement/éradication/restauration), et les recommandations préventives.',
      status: 'production',
    },
  ],
  POLITIQUE_IA: [
    {
      id: 'politique-ia-v1',
      name: 'Prise de besoin Politique d\'utilisation de l\'IA v1',
      description:
        'Questionnaire complet pour élaborer une politique d\'utilisation de l\'IA alignée sur NIST AI RMF, ITIL 4, COBIT 2019 et ISO 27001. Couvre : gouvernance IA, cartographie des risques, mesure et gestion des risques, utilisations autorisées, éthique, conformité, et responsabilités.',
      status: 'production',
    },
  ],
  INCIDENT_RESPONSE_POLICY: [
    {
      id: 'politique-gestion-incidents-v1',
      name: 'Prise de besoin Politique de gestion des incidents v1',
      description:
        'Questionnaire complet pour élaborer une politique de gestion des incidents TI conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Document maître définissant les objectifs, portée, gouvernance, enjeux et responsabilités.',
      status: 'production',
    },
  ],
  INFORMATION_SECURITY_POLICY: [
    {
      id: 'politique-securite-information-v1',
      name: 'Prise de besoin Politique de sécurité de l\'information v1',
      description:
        'Questionnaire complet pour élaborer une politique de sécurité de l\'information conforme ISO 27001, ITIL 4, COBIT 2019 et NIST CSF 2.0. Document maître de référence que le plan d\'incident et autres politiques doivent respecter.',
      status: 'production',
    },
  ],
  INCIDENT_CLASSIFICATION_POLICY: [
    {
      id: 'politique-classification-incidents-v1',
      name: 'Prise de besoin Politique de classification des incidents v1',
      description:
        'Questionnaire complet pour élaborer une politique de classification des incidents conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Définit la sévérité, l\'impact, l\'urgence et les niveaux de priorité (P1-P4 ITIL / CAT 1-5 NIST).',
      status: 'production',
    },
  ],
  INCIDENT_ESCALATION_POLICY: [
    {
      id: 'politique-escalade-incidents-v1',
      name: 'Prise de besoin Politique d\'escalade des incidents v1',
      description:
        'Questionnaire complet pour élaborer une politique d\'escalade des incidents conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Définit les responsables, délais d\'escalade, critères et processus de communication.',
      status: 'production',
    },
  ],
  INCIDENT_RESPONSE_PLAN: [
    {
      id: 'plan-intervention-incident-v1',
      name: 'Prise de besoin Plan d\'intervention en cas d\'incident (IRP) v1',
      description:
        'Questionnaire complet pour élaborer un plan d\'intervention en cas d\'incident conforme NIST CSF 2.0, ITIL 4, ISO 27001 et COBIT 2019. Définit les procédures, rôles, outils et workflows pour la détection, analyse, confinement, éradication et restauration.',
      status: 'production',
    },
  ],
  MAJOR_INCIDENT_PLAN: [
    {
      id: 'plan-incident-majeur-v1',
      name: 'Prise de besoin Plan d\'incident majeur v1',
      description:
        'Questionnaire complet pour élaborer un plan d\'incident majeur conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Définit les procédures d\'activation, l\'équipe d\'intervention, les workflows critiques et les processus de communication d\'urgence.',
      status: 'production',
    },
  ],
  INCIDENT_COMMUNICATION_PLAN: [
    {
      id: 'plan-communication-incident-v1',
      name: 'Prise de besoin Plan de communication d\'incident v1',
      description:
        'Questionnaire complet pour élaborer un plan de communication d\'incident conforme NIST CSF 2.0, ITIL 4, ISO 27001 et COBIT 2019. Définit les canaux, gabarits, audiences, messages et processus de communication interne et externe.',
      status: 'production',
    },
  ],
  ITSCM_PLAN: [
    {
      id: 'plan-continuite-ti-v1',
      name: 'Prise de besoin Plan de continuité TI (ITSCM) v1',
      description:
        'Questionnaire complet pour élaborer un plan de continuité TI conforme ITIL 4, COBIT 2019, ISO 27001 et ISO 22301. Définit les stratégies de sauvegarde, restauration, sites de secours et processus de continuité.',
      status: 'production',
    },
  ],
  PRIVACY_INCIDENT_PLAN: [
    {
      id: 'plan-incident-vie-privee-v1',
      name: 'Prise de besoin Plan d\'incident vie privée / confidentialité v1',
      description:
        'Questionnaire complet pour élaborer un plan d\'incident vie privée conforme Loi 25, RGPD, ISO 27001 et ITIL 4. Définit les procédures de détection, évaluation, notification CAI, avis aux personnes concernées et mesures correctives.',
      status: 'production',
    },
  ],
  INCIDENT_MANAGEMENT_PROCEDURE: [
    {
      id: 'procedure-gestion-incidents-v1',
      name: 'Prise de besoin Procédure de gestion des incidents (ITIL) v1',
      description:
        'Questionnaire complet pour élaborer une procédure de gestion des incidents conforme ITIL 4, COBIT 2019 et ISO 27001. Définit les étapes, workflows, rôles et responsabilités pour la détection, enregistrement, classification, résolution et fermeture des incidents.',
      status: 'production',
    },
  ],
  SECURITY_INCIDENT_RESPONSE_PROCEDURE: [
    {
      id: 'procedure-reponse-incidents-securite-v1',
      name: 'Prise de besoin Procédure de réponse aux incidents de sécurité (NIST) v1',
      description:
        'Questionnaire complet pour élaborer une procédure de réponse aux incidents de sécurité conforme NIST CSF 2.0, ITIL 4 et ISO 27001. Définit les étapes, workflows et actions pour la détection, analyse, confinement, éradication et restauration des incidents de sécurité.',
      status: 'production',
    },
  ],
  ESCALATION_PROCEDURE: [
    {
      id: 'procedure-escalade-v1',
      name: 'Prise de besoin Procédure d\'escalade v1',
      description:
        'Questionnaire complet pour élaborer une procédure d\'escalade conforme ITIL 4, COBIT 2019 et ISO 27001. Définit les critères, délais, responsables et workflows pour l\'escalade hiérarchique et fonctionnelle des incidents.',
      status: 'production',
    },
  ],
  EVIDENCE_MANAGEMENT_PROCEDURE: [
    {
      id: 'procedure-gestion-preuves-v1',
      name: 'Prise de besoin Procédure de gestion des preuves v1',
      description:
        'Questionnaire complet pour élaborer une procédure de gestion des preuves conforme ITIL 4, ISO 27001 et NIST CSF 2.0. Définit les processus de collecte, préservation, analyse et documentation des preuves numériques selon les standards forensiques.',
      status: 'production',
    },
  ],
  POST_INCIDENT_REVIEW: [
    {
      id: 'post-incident-review-v1',
      name: 'Prise de besoin Post-Incident Review (PIR) v1',
      description:
        'Questionnaire complet pour élaborer un Post-Incident Review conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Définit l\'analyse des causes, la réponse, les impacts et les leçons apprises d\'un incident.',
      status: 'production',
    },
  ],
  AFTER_ACTION_REPORT: [
    {
      id: 'after-action-report-v1',
      name: 'Prise de besoin After-Action Report (AAR) v1',
      description:
        'Questionnaire complet pour élaborer un After-Action Report conforme NIST CSF 2.0, ITIL 4 et ISO 27001. Documente la chronologie, les actions prises, les résultats et les recommandations.',
      status: 'production',
    },
  ],
  LESSONS_LEARNED_REPORT: [
    {
      id: 'rapport-lecons-apprises-v1',
      name: 'Prise de besoin Rapport de leçons apprises v1',
      description:
        'Questionnaire complet pour élaborer un Rapport de leçons apprises conforme ITIL 4, COBIT 2019 et ISO 27001. Documente les bonnes pratiques identifiées, les améliorations à apporter et les actions correctives.',
      status: 'production',
    },
  ],
};

// Emails avec restrictions (mêmes droits - Administration, RH, Infonuagique)
const RESTRICTED_USER_EMAILS = [
  'richard.delisle@bzinc.ca',
  'emilie.quintal@bzinc.ca',
  'melissa.pelletier@bzinc.ca'
];

// Accès restreint "Cybersécurité seulement" dans la gestion documentaire
const CYBER_ONLY_EMAILS = [
  'mikael.drouin-bouchard@bzinc.ca',
  'philippe.bolduc@bzinc.ca',
];

// Domaines autorisés pour les utilisateurs avec restrictions dans la gestion documentaire
const RESTRICTED_USER_ALLOWED_DOMAINS = ['Administration', 'RH', 'Infonuagique'];

// Domaines autorisés pour Cyber-only
const CYBER_ONLY_ALLOWED_DOMAINS = ['Cybersécurité'];

export default function GestionDocumentaire() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const domainFromUrl = searchParams.get('domain');
  const assetFromUrl = searchParams.get('asset');
  
  // Vérifier si l'utilisateur a des restrictions
  const isRestrictedUser = user?.email && RESTRICTED_USER_EMAILS.includes(user.email.toLowerCase());
  const isCyberOnlyUser = user?.email && CYBER_ONLY_EMAILS.includes(user.email.toLowerCase());
  
  // Nouvelle structure : Section principale + sous-onglet
  const [activeSection, setActiveSection] = useState<'gabarits' | 'livrables'>('gabarits');
  const [activeGabaritsTab, setActiveGabaritsTab] = useState<'documents' | 'formulaires'>('documents');
  const [activeLivrablesTab, setActiveLivrablesTab] = useState<'documents' | 'formulaires'>('documents');
  const [highlightedAsset, setHighlightedAsset] = useState<string | null>(assetFromUrl);

  // Filtres Types
  const [typeSearch, setTypeSearch] = useState('');
  const [typeDomainFilter, setTypeDomainFilter] = useState<string>(domainFromUrl || 'tous');
  
  // Appliquer le filtre de domaine depuis l'URL au chargement
  useEffect(() => {
    if (domainFromUrl) {
      setTypeDomainFilter(domainFromUrl);
    }
    if (assetFromUrl) {
      setHighlightedAsset(assetFromUrl);
    }
    if (domainFromUrl || assetFromUrl) {
      // Nettoyer l'URL après avoir appliqué le contexte
      setSearchParams({}, { replace: true });
    }
  }, [assetFromUrl, domainFromUrl, setSearchParams]);
  const [typeAvailabilityFilter, setTypeAvailabilityFilter] = useState<'tous' | 'production' | 'coming_soon' | 'archived'>('tous');
  const [typeDanyFilter, setTypeDanyFilter] = useState<string>('tous');
  const [typeSpecialistFilter, setTypeSpecialistFilter] = useState<string>('tous');

  // Filtres Gabarits
  const [intakeSearch, setIntakeSearch] = useState('');
  const [intakeTypeFilter, setIntakeTypeFilter] = useState<string>('tous');
  const [intakeDomainFilter, setIntakeDomainFilter] = useState<string>('tous');
  const [intakeAvailabilityFilter, setIntakeAvailabilityFilter] = useState<'tous' | 'production' | 'coming_soon' | 'archived'>('tous');

  // Forcer le domaine Cybersécurité pour les usagers "cyber-only"
  useEffect(() => {
    if (!isCyberOnlyUser) return;
    setTypeDomainFilter('Cybersécurité');
    setIntakeDomainFilter('Cybersécurité');
  }, [isCyberOnlyUser]);

  // Modals Document Info
  const [isPlanDirecteurModalOpen, setIsPlanDirecteurModalOpen] = useState(false);
  const [planDirecteurInitialTab, setPlanDirecteurInitialTab] = useState<'explainer' | 'prompts'>('explainer');
  const [isPlanDirecteurTechniqueModalOpen, setIsPlanDirecteurTechniqueModalOpen] = useState(false);
  const [isRoadmap24ModalOpen, setIsRoadmap24ModalOpen] = useState(false);
  const [isObjectifsKPIModalOpen, setIsObjectifsKPIModalOpen] = useState(false);
  const [isCahierChargeModalOpen, setIsCahierChargeModalOpen] = useState(false);
  const [isMatriceRACIModalOpen, setIsMatriceRACIModalOpen] = useState(false);
  const [isPlanBudgetaireModalOpen, setIsPlanBudgetaireModalOpen] = useState(false);
  const [isPlanBudgetaire5AnsModalOpen, setIsPlanBudgetaire5AnsModalOpen] = useState(false);
  const [isRegistreAmeliorationModalOpen, setIsRegistreAmeliorationModalOpen] = useState(false);
  const [isRegistreRisquesModalOpen, setIsRegistreRisquesModalOpen] = useState(false);
  const [isOffreServiceBZModalOpen, setIsOffreServiceBZModalOpen] = useState(false);
  const [isPolitiqueCybersecuriteModalOpen, setIsPolitiqueCybersecuriteModalOpen] = useState(false);
  const [isRapportIncidentModalOpen, setIsRapportIncidentModalOpen] = useState(false);
  const [isPolitiqueIAModalOpen, setIsPolitiqueIAModalOpen] = useState(false);
  const [isPolitiqueGestionIncidentsModalOpen, setIsPolitiqueGestionIncidentsModalOpen] = useState(false);
  const [isPolitiqueSecuriteInformationModalOpen, setIsPolitiqueSecuriteInformationModalOpen] = useState(false);
  const [isPolitiqueClassificationIncidentsModalOpen, setIsPolitiqueClassificationIncidentsModalOpen] = useState(false);
  const [isPolitiqueEscaladeIncidentsModalOpen, setIsPolitiqueEscaladeIncidentsModalOpen] = useState(false);
  const [isPlanInterventionIncidentModalOpen, setIsPlanInterventionIncidentModalOpen] = useState(false);
  const [isPlanIncidentMajeurModalOpen, setIsPlanIncidentMajeurModalOpen] = useState(false);
  const [isPlanCommunicationIncidentModalOpen, setIsPlanCommunicationIncidentModalOpen] = useState(false);
  const [isPlanContinuiteTiModalOpen, setIsPlanContinuiteTiModalOpen] = useState(false);
  const [isPlanIncidentViePriveeModalOpen, setIsPlanIncidentViePriveeModalOpen] = useState(false);
  const [isProcedureGestionIncidentsModalOpen, setIsProcedureGestionIncidentsModalOpen] = useState(false);
  const [isProcedureReponseIncidentsSecuriteModalOpen, setIsProcedureReponseIncidentsSecuriteModalOpen] = useState(false);
  const [isProcedureEscaladeModalOpen, setIsProcedureEscaladeModalOpen] = useState(false);
  const [isProcedureGestionPreuvesModalOpen, setIsProcedureGestionPreuvesModalOpen] = useState(false);
  const [isPostIncidentReviewModalOpen, setIsPostIncidentReviewModalOpen] = useState(false);
  const [isAfterActionReportModalOpen, setIsAfterActionReportModalOpen] = useState(false);
  const [isRapportLeconsApprisesModalOpen, setIsRapportLeconsApprisesModalOpen] = useState(false);

  // Modal Prise de besoin (exemple client fictif)
  const [isPriseDeBesoinModalOpen, setIsPriseDeBesoinModalOpen] = useState(false);
  const [priseDeBesoinInitialTab, setPriseDeBesoinInitialTab] = useState<'structure' | 'rapport'>('rapport');
  const [isPriseDeBesoinWizardOpen, setIsPriseDeBesoinWizardOpen] = useState(false);
  const [isPriseDeBesoinPolitiqueCybersecuriteOpen, setIsPriseDeBesoinPolitiqueCybersecuriteOpen] = useState(false);
  const [isPriseDeBesoinPlanDirecteurTechniqueOpen, setIsPriseDeBesoinPlanDirecteurTechniqueOpen] = useState(false);
  const [isPriseDeBesoinPlanBudgetaire5AnsOpen, setIsPriseDeBesoinPlanBudgetaire5AnsOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('pdti-standard-pme');
  const [selectedTemplateVersion, setSelectedTemplateVersion] = useState<string>('1.0.0');
  const [selectedClient2026Schema, setSelectedClient2026Schema] = useState<number | undefined>(undefined);
  const [isExportExcelModalOpen, setIsExportExcelModalOpen] = useState(false);
  const [editingBesoinId, setEditingBesoinId] = useState<number | undefined>(undefined);
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [partiesPrenantes, setPartiesPrenantes] = useState<PartiePrenante[]>([]);
  const [docAuditModalId, setDocAuditModalId] = useState<number | null>(null);
  const [docAuditLoading, setDocAuditLoading] = useState(false);
  const [docAuditError, setDocAuditError] = useState<string | null>(null);
  const [docAuditHistory, setDocAuditHistory] = useState<any[] | null>(null);
  const [linkModalTemplate, setLinkModalTemplate] = useState<{ id: string; name: string } | null>(null);
  const [documentWizardType, setDocumentWizardType] = useState<string | null>(null);
  const [viewingDocumentId, setViewingDocumentId] = useState<number | null>(null);
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null);

  // Documents créés
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [documentsClientFilter, setDocumentsClientFilter] = useState<string>('tous');
  const [documentsTypeFilter, setDocumentsTypeFilter] = useState<string>('tous');
  const [documentsStatusFilter, setDocumentsStatusFilter] = useState<string>('tous');
  const [documentsSearch, setDocumentsSearch] = useState<string>('');
  const [documentsDanyFilter, setDocumentsDanyFilter] = useState<string>('tous');
  const [documentsSpecialistFilter, setDocumentsSpecialistFilter] = useState<string>('tous');

  // Filtres Prises de besoin
  const [besoinSearch, setBesoinSearch] = useState('');
  const [besoinClientFilter, setBesoinClientFilter] = useState<string>('tous');
  const [besoinVisibilityFilter, setBesoinVisibilityFilter] = useState<string>('tous');
  const [besoinTemplateFilter, setBesoinTemplateFilter] = useState<string>('tous');
  const [allBesoins, setAllBesoins] = useState<any[]>([]);

  // Charger tous les besoins pour le compteur
  useEffect(() => {
    const loadBesoins = async () => {
      try {
        const besoins = await getAllBesoins();
        setAllBesoins(Array.isArray(besoins) ? besoins : []);
      } catch (error) {
        console.error('Erreur chargement besoins pour compteur:', error);
        setAllBesoins([]);
      }
    };
    if (activeSection === 'livrables' && activeLivrablesTab === 'formulaires') {
      loadBesoins();
    }
  }, [activeSection, activeLivrablesTab]);

  // Calculer le nombre de besoins filtrés
  const filteredBesoinsCount = useMemo(() => {
    return allBesoins.filter((b) => {
      const matchesSearch =
        !besoinSearch ||
        b.nom?.toLowerCase().includes(besoinSearch.toLowerCase()) ||
        b.description?.toLowerCase().includes(besoinSearch.toLowerCase()) ||
        (b as any).client_nom?.toLowerCase().includes(besoinSearch.toLowerCase());
      const matchesClient =
        besoinClientFilter === 'tous' || b.client_id.toString() === besoinClientFilter;
      const matchesVisibility =
        besoinVisibilityFilter === 'tous'
          ? true
          : besoinVisibilityFilter === 'visible'
          ? b.showpriseclient !== false
          : b.showpriseclient === false;
      const matchesTemplate =
        besoinTemplateFilter === 'archives'
          ? (b as any).archived === true
          : (besoinTemplateFilter === 'tous' || b.template_id === besoinTemplateFilter) && (b as any).archived !== true;
      return matchesSearch && matchesClient && matchesVisibility && matchesTemplate;
    }).length;
  }, [allBesoins, besoinSearch, besoinClientFilter, besoinVisibilityFilter, besoinTemplateFilter]);

  // Charger les clients au montage
  useEffect(() => {
    const loadClients = async () => {
      try {
        const clientsList = await getAllClients();
        setClients(clientsList.filter((c) => c.actif !== false).sort((a, b) => {
          const nomA = a.nom?.toLowerCase() || '';
          const nomB = b.nom?.toLowerCase() || '';
          return nomA.localeCompare(nomB, 'fr', { sensitivity: 'base' });
        }));
      } catch (error) {
        console.error('Erreur chargement clients:', error);
      }
    };
    loadClients();
  }, []);

  // Charger les parties prenantes (pour afficher avatars/auteurs)
  useEffect(() => {
    (async () => {
      try {
        const pp = await getPartiesPrenantes('all');
        setPartiesPrenantes(pp || []);
      } catch (error) {
        console.error('Erreur chargement parties prenantes:', error);
        setPartiesPrenantes([]);
      }
    })();
  }, []);

  const ppById = useMemo(() => {
    const map = new Map<number, PartiePrenante>();
    (partiesPrenantes || []).forEach((p) => {
      if (typeof p.id === 'number') map.set(p.id, p);
    });
    return map;
  }, [partiesPrenantes]);

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getPPLabel = (ppId?: number | null) => {
    if (!ppId) return 'N/A';
    const p = ppById.get(ppId);
    if (!p) return `#${ppId}`;
    return `${p.prenom || ''} ${p.nom || ''}`.trim() || `#${ppId}`;
  };

  const UserAvatar = ({ ppId, title }: { ppId?: number | null; title: string }) => {
    if (!ppId) {
      return (
        <div className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-500" title={title}>
          ?
        </div>
      );
    }
    const p = ppById.get(ppId);
    const initials =
      p ? `${(p.prenom || '').slice(0, 1)}${(p.nom || '').slice(0, 1)}`.toUpperCase() : `${ppId}`.slice(0, 2);
    const canShowAvatar = !!p?.has_avatar;
    return (
      <div className="w-7 h-7 rounded-full overflow-hidden border border-gray-200 bg-gray-50" title={title}>
        {canShowAvatar ? (
          <img src={getPartiePrenanteAvatarUrl(ppId)} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-gray-600">
            {initials}
          </div>
        )}
      </div>
    );
  };

  const openDocAuditModal = async (documentId: number) => {
    setDocAuditModalId(documentId);
    setDocAuditLoading(true);
    setDocAuditError(null);
    setDocAuditHistory(null);
    try {
      const hist = await getDocumentHistorique(documentId);
      setDocAuditHistory(hist || []);
    } catch (e: any) {
      setDocAuditError(e?.message || 'Erreur chargement historique');
    } finally {
      setDocAuditLoading(false);
    }
  };

  // Charger tous les documents créés lorsque l'onglet documents est actif
  // On inclut les archivés pour que le filtre Statut "Archivés" puisse les afficher (le filtre côté client les masque sinon)
  useEffect(() => {
    const loadDocuments = async () => {
      if (activeSection !== 'livrables' || activeLivrablesTab !== 'documents') return;
      setDocumentsLoading(true);
      setDocumentsError(null);
      try {
        const allDocs = await getAllDocuments(true); // true = inclure les archivés (affichés uniquement si Statut = Archivés)
        setDocuments(allDocs);
      } catch (err: any) {
        console.error('Erreur chargement documents:', err);
        setDocumentsError(err?.message || 'Erreur lors du chargement des documents créés');
      } finally {
        setDocumentsLoading(false);
      }
    };

    loadDocuments();
  }, [activeSection, activeLivrablesTab]);

  // Filtrer les domaines selon l'utilisateur
  const allDomains = useMemo(() => {
    const domains = Array.from(new Set(DOCUMENT_TYPES.map((t) => t.domain)));
    if (isCyberOnlyUser) {
      return domains.filter(d => CYBER_ONLY_ALLOWED_DOMAINS.includes(d));
    } else if (isRestrictedUser) {
      // Utilisateurs avec restrictions voient seulement Administration, RH, et Infonuagique
      return domains.filter(d => RESTRICTED_USER_ALLOWED_DOMAINS.includes(d));
    }
    return domains;
  }, [isRestrictedUser, isCyberOnlyUser]);

  const filteredDocumentTypes = useMemo(() => {
    return DOCUMENT_TYPES.filter((t) => {
      // Filtrer par domaine autorisé pour Cyber-only
      if (isCyberOnlyUser && !CYBER_ONLY_ALLOWED_DOMAINS.includes(t.domain)) {
        return false;
      }
      // Filtrer par domaine autorisé pour les utilisateurs avec restrictions
      if (isRestrictedUser && !RESTRICTED_USER_ALLOWED_DOMAINS.includes(t.domain)) {
        return false;
      }
      
      const matchesSearch =
        !typeSearch ||
        t.title.toLowerCase().includes(typeSearch.toLowerCase()) ||
        t.code.toLowerCase().includes(typeSearch.toLowerCase());
      const matchesDomain = typeDomainFilter === 'tous' || t.domain === typeDomainFilter;
      const matchesAvailability =
        typeAvailabilityFilter === 'tous'
          ? t.status !== 'archived'
          : t.status === typeAvailabilityFilter;
      const matchesDany =
        typeDanyFilter === 'tous' || 
        (typeDanyFilter === 'non_renseigne' && (!t.approved_dany || t.approved_dany === 0)) ||
        (typeDanyFilter !== 'non_renseigne' && t.approved_dany && String(t.approved_dany) === typeDanyFilter);
      const matchesSpecialist =
        typeSpecialistFilter === 'tous' || 
        (typeSpecialistFilter === 'non_renseigne' && (!t.approved_specialist || t.approved_specialist === 0)) ||
        (typeSpecialistFilter !== 'non_renseigne' && t.approved_specialist && String(t.approved_specialist) === typeSpecialistFilter);
      return matchesSearch && matchesDomain && matchesAvailability && matchesDany && matchesSpecialist;
    });
  }, [typeSearch, typeDomainFilter, typeAvailabilityFilter, typeDanyFilter, typeSpecialistFilter, isRestrictedUser, isCyberOnlyUser]);

  const flattenedIntakes = useMemo(() => {
    return Object.entries(INTAKE_TEMPLATES_BY_TYPE).flatMap(([code, templates]) => {
      const type = DOCUMENT_TYPES.find((t) => t.code === code);
      return templates.map((tpl) => ({
        ...tpl,
        documentTypeCode: code,
        documentTypeTitle: type?.title || code,
        domain: type?.domain || 'Général',
      }));
    });
  }, []);

  const filteredIntakes = useMemo(() => {
    const filtered = flattenedIntakes.filter((tpl) => {
      // Filtrer par domaine autorisé pour Cyber-only
      if (isCyberOnlyUser && !CYBER_ONLY_ALLOWED_DOMAINS.includes(tpl.domain)) {
        return false;
      }
      // Filtrer par domaine autorisé pour les utilisateurs avec restrictions
      if (isRestrictedUser && !RESTRICTED_USER_ALLOWED_DOMAINS.includes(tpl.domain)) {
        return false;
      }
      
      const matchesSearch =
        !intakeSearch || tpl.name.toLowerCase().includes(intakeSearch.toLowerCase());
      const matchesType = intakeTypeFilter === 'tous' || tpl.documentTypeCode === intakeTypeFilter;
      const matchesDomain = intakeDomainFilter === 'tous' || tpl.domain === intakeDomainFilter;
      const matchesAvailability =
        intakeAvailabilityFilter === 'tous'
          ? tpl.status !== 'archived'
          : tpl.status === intakeAvailabilityFilter;
      return matchesSearch && matchesType && matchesDomain && matchesAvailability;
    });
    
    // Trier : production en premier, puis ordre alphabétique
    return filtered.sort((a, b) => {
      // Production d'abord
      if (a.status === 'production' && b.status !== 'production') return -1;
      if (a.status !== 'production' && b.status === 'production') return 1;
      // Puis ordre alphabétique par nom
      return a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
    });
  }, [flattenedIntakes, intakeSearch, intakeTypeFilter, intakeDomainFilter, intakeAvailabilityFilter, isRestrictedUser, isCyberOnlyUser]);

  // Documents filtrés pour l'onglet "Documents créés"
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const client = clients.find((c) => c.id === doc.client_id);
      const type = DOCUMENT_TYPES.find((t) => t.code === doc.document_type_code);

      if (isCyberOnlyUser && type?.domain !== 'Cybersécurité') return false;

      const matchesClient =
        documentsClientFilter === 'tous' || String(doc.client_id) === documentsClientFilter;
      const matchesType =
        documentsTypeFilter === 'tous' || doc.document_type_code === documentsTypeFilter;
      const matchesStatus =
        documentsStatusFilter === 'tous'
          ? !doc.archived
          : documentsStatusFilter === 'visible'
          ? doc.showpriseclient && !doc.archived
          : documentsStatusFilter === 'presented'
          ? doc.presented_client && !doc.archived
          : documentsStatusFilter === 'archives'
          ? doc.archived
          : true;
      const matchesSearch =
        !documentsSearch ||
        (doc.title && doc.title.toLowerCase().includes(documentsSearch.toLowerCase())) ||
        (client?.nom && client.nom.toLowerCase().includes(documentsSearch.toLowerCase())) ||
        (type?.title && type.title.toLowerCase().includes(documentsSearch.toLowerCase()));
      const matchesDany =
        documentsDanyFilter === 'tous' || 
        (documentsDanyFilter === 'non_renseigne' && !doc.approved_dany) ||
        (documentsDanyFilter !== 'non_renseigne' && doc.approved_dany && String(doc.approved_dany) === documentsDanyFilter);
      const matchesSpecialist =
        documentsSpecialistFilter === 'tous' || 
        (documentsSpecialistFilter === 'non_renseigne' && !doc.approved_specialist) ||
        (documentsSpecialistFilter !== 'non_renseigne' && doc.approved_specialist && String(doc.approved_specialist) === documentsSpecialistFilter);

      return matchesClient && matchesType && matchesStatus && matchesSearch && matchesDany && matchesSpecialist;
    });
  }, [
    documents,
    clients,
    documentsClientFilter,
    documentsTypeFilter,
    documentsStatusFilter,
    documentsSearch,
    documentsDanyFilter,
    documentsSpecialistFilter,
    isCyberOnlyUser,
  ]);

  return (
    <div className="min-h-screen">
      <Header
        title="Gestion Documentaire PRIsME"
        subtitle="Concevoir les types de documents, leurs prises de besoin et leurs squelettes normés avant la mise en production"
      />

      <div className="px-8 pb-12 space-y-8">
        {/* Flowchart méthodologie */}
        <MethodologyFlowchart />

        {/* Sections principales avec visuel sharp */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Section Gabarits */}
          <div
            className={`rounded-2xl border-2 transition-all ${
              activeSection === 'gabarits' 
                ? 'border-blue-500 bg-blue-50 shadow-lg' 
                : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
            }`}
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => setActiveSection('gabarits')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveSection('gabarits');
                }
              }}
              className="w-full p-6 text-left cursor-pointer"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                  activeSection === 'gabarits' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-blue-100 text-blue-600'
                }`}>
                  <FileText className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Gabarits</h2>
                  <p className="text-sm text-gray-600">Modèles et structures de base</p>
                </div>
              </div>
              {activeSection === 'gabarits' && (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveGabaritsTab('documents');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeGabaritsTab === 'documents'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-blue-50'
                    }`}
                  >
                    Documents
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveGabaritsTab('formulaires');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeGabaritsTab === 'formulaires'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-blue-50'
                    }`}
                  >
                    Formulaires
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Section Livrables créés */}
          <div
            className={`rounded-2xl border-2 transition-all ${
              activeSection === 'livrables' 
                ? 'border-emerald-500 bg-emerald-50 shadow-lg' 
                : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-md'
            }`}
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => setActiveSection('livrables')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveSection('livrables');
                }
              }}
              className="w-full p-6 text-left cursor-pointer"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                  activeSection === 'livrables' 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-emerald-100 text-emerald-600'
                }`}>
                  <FileCheck className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Livrables créés</h2>
                  <p className="text-sm text-gray-600">Documents et formulaires générés</p>
                </div>
              </div>
              {activeSection === 'livrables' && (
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveLivrablesTab('documents');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeLivrablesTab === 'documents'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-emerald-50'
                    }`}
                  >
                    Documents
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveLivrablesTab('formulaires');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeLivrablesTab === 'formulaires'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-emerald-50'
                    }`}
                  >
                    Formulaires
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 1 – Types de documents */}
        {activeSection === 'gabarits' && activeGabaritsTab === 'documents' && (
          <section className="space-y-4">
            {(typeDomainFilter === 'TI' || highlightedAsset === 'bz-telecom-network') && (
              <div className={`overflow-hidden rounded-3xl border shadow-sm ${
                highlightedAsset === 'bz-telecom-network'
                  ? 'border-orange-300 bg-linear-to-br from-orange-50 via-white to-amber-50'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-start">
                  <div className="flex-1">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-[11px] font-semibold text-orange-800">
                        Documentation TI
                      </span>
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-700">
                        BZ Telecom
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Plan reseau BZ Telecom</h3>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
                      Plan visuel des 2 sites BZ et Serko: FortiGate, switchs WAN, VLANs, transport inter-site et
                      serveurs. Place ici pour que l&apos;equipe retrouve rapidement l&apos;architecture reseau dans la
                      documentation PRIsME.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <a
                        href="/bz-telecom-plan-reseau.jpg"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-bz-blue/30 bg-white px-4 py-2 text-xs font-semibold text-bz-blue transition-colors hover:bg-bz-blue hover:text-white"
                      >
                        <Eye className="w-4 h-4" />
                        Voir pleine grandeur
                      </a>
                      <a
                        href="/bz-telecom-plan-reseau.jpg"
                        download="Plan_Reseau_BZ_Telecom.jpg"
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-white px-4 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-500 hover:text-white"
                      >
                        <Download className="w-4 h-4" />
                        Telecharger l&apos;image
                      </a>
                    </div>
                  </div>

                  <a
                    href="/bz-telecom-plan-reseau.jpg"
                    target="_blank"
                    rel="noreferrer"
                    className="block overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-transform hover:scale-[1.01] lg:w-[560px]"
                  >
                    <img
                      src="/bz-telecom-plan-reseau.jpg"
                      alt="Plan reseau BZ Telecom"
                      className="h-auto w-full object-cover"
                    />
                  </a>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Types de documents PRIsME</h3>
                <p className="text-xs text-gray-600">
                  Catalogue des types de documents que PRIsME peut produire: Plan Directeur, ODS, Plan Cyber, et tous les
                  futurs livrables stratégiques. On garde ici une vue d&apos;ensemble, filtrable par domaine.
                </p>
              </div>

              {/* Filtres */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Recherche</label>
                  <input
                    type="text"
                    value={typeSearch}
                    onChange={(e) => setTypeSearch(e.target.value)}
                    placeholder="Code ou titre..."
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Domaine</label>
                  <select
                    value={typeDomainFilter}
                    onChange={(e) => setTypeDomainFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Tous les domaines</option>
                    {allDomains.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Statut</label>
                  <select
                    value={typeAvailabilityFilter}
                    onChange={(e) =>
                      setTypeAvailabilityFilter(e.target.value as 'tous' | 'production' | 'coming_soon' | 'archived')
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Tous les statuts</option>
                    <option value="production">Production</option>
                    <option value="coming_soon">À venir</option>
                    <option value="archived">Archivés</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">⭐ Dany</label>
                  <select
                    value={typeDanyFilter}
                    onChange={(e) => setTypeDanyFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Toutes les étoiles</option>
                    <option value="non_renseigne">Non renseigné</option>
                    <option value="1">1 ⭐ - Premier brouillon</option>
                    <option value="2">2 ⭐ - Commencé mais pas prêt</option>
                    <option value="3">3 ⭐ - Peut être en prod</option>
                    <option value="4">4 ⭐ - Satisfait mais manque qqch</option>
                    <option value="5">5 ⭐ - Tout est sur la coche</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">⭐ Spécialiste</label>
                  <select
                    value={typeSpecialistFilter}
                    onChange={(e) => setTypeSpecialistFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Toutes les étoiles</option>
                    <option value="non_renseigne">Non renseigné</option>
                    <option value="1">1 ⭐ - Premier brouillon</option>
                    <option value="2">2 ⭐ - Commencé mais pas prêt</option>
                    <option value="3">3 ⭐ - Peut être en prod</option>
                    <option value="4">4 ⭐ - Satisfait mais manque qqch</option>
                    <option value="5">5 ⭐ - Tout est sur la coche</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end">
                <span className="text-[11px] text-gray-500 font-medium">
                  {filteredDocumentTypes.length} {filteredDocumentTypes.length === 1 ? 'type' : 'types'}
                </span>
              </div>
            </div>

            {/* Listing types (cartes + statut) */}
            <div className="bg-transparent rounded-2xl">
              <div className="space-y-2">
                {filteredDocumentTypes.map((docType) => (
                  <div
                    key={docType.id}
                    className="px-5 py-4 text-xs bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        <div className="w-8 h-8 rounded-xl bg-bz-blue/10 flex items-center justify-center text-bz-blue">
                          <FileText className="w-4 h-4" />
                        </div>
                      </div>
                      <div>
                        <p className="mb-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              docType.status === 'production'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : docType.status === 'archived'
                                ? 'bg-gray-100 text-gray-700 border border-gray-300'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}
                          >
                            {docType.status === 'production' ? 'Production' : docType.status === 'archived' ? 'Archivé' : 'À venir'}
                          </span>
                        </p>
                        <p className="text-[11px] font-semibold text-bz-blue uppercase tracking-wide mb-0.5">
                          {docType.code}
                        </p>
                        <h4 className="text-sm font-bold text-gray-900 mb-1">{docType.title}</h4>
                        <p className="text-xs text-gray-600 mb-1">{docType.description}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {docType.frameworks.map((fw) => (
                            <span
                              key={fw}
                              className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-[10px] font-medium text-gray-700"
                            >
                              {fw}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col md:items-end gap-3 text-[11px]">
                      <div className="flex items-center gap-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-gray-500 font-medium">Dany</span>
                          <StarRating rating={docType.approved_dany || undefined} size="sm" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] text-gray-500 font-medium">Spécialiste</span>
                          <StarRating rating={docType.approved_specialist || undefined} size="sm" />
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                        {docType.domain}
                      </span>
                      <div className="flex flex-wrap gap-2 justify-end">
                        {(docType.code === 'PRISE_BESOIN_CLIENT_2026' || docType.code === 'PRISE_BESOIN_CLIENT_2026_ANALYSE' || docType.code === 'PRISME_KMI' || docType.code === 'PRISME_KMI_CIBLE' || docType.code === 'PLAN_DIRECTEUR' || docType.code === 'PLAN_DIRECTEUR_5_ANS_V2' || docType.code === 'PLAN_DIRECTEUR_TECHNIQUE' || docType.code === 'ROADMAP_24M' || docType.code === 'OBJECTIFS_KPI_TI' || docType.code === 'CAHIER_CHARGE' || docType.code === 'MATRICE_RACI_TI' || docType.code === 'PLAN_BUDGETAIRE_TI_N1' || docType.code === 'PLAN_BUDGETAIRE_5_ANS' || docType.code === 'REGISTRE_AMELIORATION_CONTINUE_TI' || docType.code === 'REGISTRE_RISQUES_TI' || docType.code === 'OFFRE_SERVICE_BZ' || docType.code === 'POLITIQUE_CYBER' || docType.code === 'RAPPORT_INCIDENT' || docType.code === 'POLITIQUE_IA' || docType.code === 'INCIDENT_RESPONSE_POLICY' || docType.code === 'INFORMATION_SECURITY_POLICY' || docType.code === 'INCIDENT_CLASSIFICATION_POLICY' || docType.code === 'INCIDENT_ESCALATION_POLICY' || docType.code === 'INCIDENT_RESPONSE_PLAN' || docType.code === 'MAJOR_INCIDENT_PLAN' || docType.code === 'INCIDENT_COMMUNICATION_PLAN' || docType.code === 'ITSCM_PLAN' || docType.code === 'PRIVACY_INCIDENT_PLAN' || docType.code === 'INCIDENT_MANAGEMENT_PROCEDURE' || docType.code === 'SECURITY_INCIDENT_RESPONSE_PROCEDURE' || docType.code === 'ESCALATION_PROCEDURE' || docType.code === 'EVIDENCE_MANAGEMENT_PROCEDURE' || docType.code === 'POST_INCIDENT_REVIEW' || docType.code === 'AFTER_ACTION_REPORT' || docType.code === 'LESSONS_LEARNED_REPORT') && (
                          <button
                            onClick={() => {
                              if (docType.code === 'PRISE_BESOIN_CLIENT_2026') {
                                setSelectedTemplateId('prise-de-besoin-client-2026');
                                setPriseDeBesoinInitialTab('structure');
                                setIsPriseDeBesoinModalOpen(true);
                              } else if (docType.code === 'PRISE_BESOIN_CLIENT_2026_ANALYSE') {
                                setSelectedTemplateId('prise-de-besoin-client-2026-analyse');
                                setPriseDeBesoinInitialTab('rapport');
                                setIsPriseDeBesoinModalOpen(true);
                              } else if (docType.code === 'PRISME_KMI') {
                                setSelectedTemplateId('prisme-kmi-v1');
                                setPriseDeBesoinInitialTab('structure');
                                setIsPriseDeBesoinModalOpen(true);
                              } else if (docType.code === 'PRISME_KMI_CIBLE') {
                                setSelectedTemplateId('prisme-kmi-cible-v1');
                                setPriseDeBesoinInitialTab('structure');
                                setIsPriseDeBesoinModalOpen(true);
                              } else if (docType.code === 'PLAN_DIRECTEUR') {
                                setPlanDirecteurInitialTab('explainer');
                                setIsPlanDirecteurModalOpen(true);
                              } else if (docType.code === 'PLAN_DIRECTEUR_5_ANS_V2') {
                                setDocumentWizardType(docType.code);
                              } else if (docType.code === 'PLAN_DIRECTEUR_TECHNIQUE') {
                                setIsPlanDirecteurTechniqueModalOpen(true);
                              } else if (docType.code === 'ROADMAP_24M') {
                                setIsRoadmap24ModalOpen(true);
                              } else if (docType.code === 'OBJECTIFS_KPI_TI') {
                                setIsObjectifsKPIModalOpen(true);
                              } else if (docType.code === 'CAHIER_CHARGE') {
                                setIsCahierChargeModalOpen(true);
                              } else if (docType.code === 'MATRICE_RACI_TI') {
                                setIsMatriceRACIModalOpen(true);
                              } else if (docType.code === 'PLAN_BUDGETAIRE_TI_N1') {
                                setIsPlanBudgetaireModalOpen(true);
                              } else if (docType.code === 'PLAN_BUDGETAIRE_5_ANS') {
                                setIsPlanBudgetaire5AnsModalOpen(true);
                              } else if (docType.code === 'REGISTRE_AMELIORATION_CONTINUE_TI') {
                                setIsRegistreAmeliorationModalOpen(true);
                              } else if (docType.code === 'REGISTRE_RISQUES_TI') {
                                setIsRegistreRisquesModalOpen(true);
                              } else if (docType.code === 'OFFRE_SERVICE_BZ') {
                                setIsOffreServiceBZModalOpen(true);
                              } else if (docType.code === 'POLITIQUE_CYBER') {
                                setIsPolitiqueCybersecuriteModalOpen(true);
                              } else if (docType.code === 'RAPPORT_INCIDENT') {
                                setIsRapportIncidentModalOpen(true);
                              } else if (docType.code === 'POLITIQUE_IA') {
                                setIsPolitiqueIAModalOpen(true);
                              } else if (docType.code === 'INCIDENT_RESPONSE_POLICY') {
                                setIsPolitiqueGestionIncidentsModalOpen(true);
                              } else if (docType.code === 'INFORMATION_SECURITY_POLICY') {
                                setIsPolitiqueSecuriteInformationModalOpen(true);
                              } else if (docType.code === 'INCIDENT_CLASSIFICATION_POLICY') {
                                setIsPolitiqueClassificationIncidentsModalOpen(true);
                              } else if (docType.code === 'INCIDENT_ESCALATION_POLICY') {
                                setIsPolitiqueEscaladeIncidentsModalOpen(true);
                              } else if (docType.code === 'INCIDENT_RESPONSE_PLAN') {
                                setIsPlanInterventionIncidentModalOpen(true);
                              } else if (docType.code === 'MAJOR_INCIDENT_PLAN') {
                                setIsPlanIncidentMajeurModalOpen(true);
                              } else if (docType.code === 'INCIDENT_COMMUNICATION_PLAN') {
                                setIsPlanCommunicationIncidentModalOpen(true);
                              } else if (docType.code === 'ITSCM_PLAN') {
                                setIsPlanContinuiteTiModalOpen(true);
                              } else if (docType.code === 'PRIVACY_INCIDENT_PLAN') {
                                setIsPlanIncidentViePriveeModalOpen(true);
                              } else if (docType.code === 'INCIDENT_MANAGEMENT_PROCEDURE') {
                                setIsProcedureGestionIncidentsModalOpen(true);
                              } else if (docType.code === 'SECURITY_INCIDENT_RESPONSE_PROCEDURE') {
                                setIsProcedureReponseIncidentsSecuriteModalOpen(true);
                              } else if (docType.code === 'ESCALATION_PROCEDURE') {
                                setIsProcedureEscaladeModalOpen(true);
                              } else if (docType.code === 'EVIDENCE_MANAGEMENT_PROCEDURE') {
                                setIsProcedureGestionPreuvesModalOpen(true);
                              } else if (docType.code === 'POST_INCIDENT_REVIEW') {
                                setIsPostIncidentReviewModalOpen(true);
                              } else if (docType.code === 'AFTER_ACTION_REPORT') {
                                setIsAfterActionReportModalOpen(true);
                              } else if (docType.code === 'LESSONS_LEARNED_REPORT') {
                                setIsRapportLeconsApprisesModalOpen(true);
                              }
                            }}
                            className="px-3 py-1.5 rounded-full border border-bz-blue/60 text-bz-blue bg-white text-[11px] font-semibold hover:bg-bz-blue hover:text-white transition-colors"
                          >
                            Ouvrir l'espace
                          </button>
                        )}
                        <button
                          onClick={() => setDocumentWizardType(docType.code)}
                          className="px-3 py-1.5 rounded-full border border-emerald-500/60 text-emerald-700 bg-white text-[11px] font-semibold hover:bg-emerald-500 hover:text-white transition-colors"
                        >
                          Créer document
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {filteredDocumentTypes.length === 0 && (
                  <div className="px-5 py-6 text-center text-[11px] text-gray-500">
                    Aucun type ne correspond aux filtres actuels.
                  </div>
                )}
              </div>
            </div>

          </section>
        )}

        {/* SECTION 2 – Documents créés */}
        {/* SECTION LIVRABLES CRÉÉS - Documents */}
        {activeSection === 'livrables' && activeLivrablesTab === 'documents' && (
          <section className="space-y-4">
            <div className="space-y-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Documents créés pour les clients</h3>
                <p className="text-xs text-gray-600">
                  Vue consolidée de tous les documents générés par PRIsME (Plan Directeur, ODS, etc.), avec filtres par
                  client, type, statut et recherche texte. Chaque carte ouvre la vue détaillée du document.
                </p>
              </div>
            </div>

            {/* Filtres documents */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Filter className="w-4 h-4" />
                  <span>Filtres sur les documents générés</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setDocumentsClientFilter('tous');
                    setDocumentsTypeFilter('tous');
                    setDocumentsStatusFilter('tous');
                    setDocumentsSearch('');
                    setDocumentsDanyFilter('tous');
                    setDocumentsSpecialistFilter('tous');
                  }}
                  className="text-[11px] text-bz-blue hover:underline"
                >
                  Réinitialiser les filtres
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-xs">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Recherche</label>
                  <input
                    type="text"
                    value={documentsSearch}
                    onChange={(e) => setDocumentsSearch(e.target.value)}
                    placeholder="Client, titre, type..."
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Client</label>
                  <select
                    value={documentsClientFilter}
                    onChange={(e) => setDocumentsClientFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Tous les clients</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Type de document</label>
                  <select
                    value={documentsTypeFilter}
                    onChange={(e) => setDocumentsTypeFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Tous les types</option>
                    {DOCUMENT_TYPES.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.code} – {t.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Statut</label>
                  <select
                    value={documentsStatusFilter}
                    onChange={(e) => setDocumentsStatusFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Tous les statuts</option>
                    <option value="visible">Visible dans le PRIsME client</option>
                    <option value="presented">Présenté au client</option>
                    <option value="archives">Archivés</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">⭐ Dany</label>
                  <select
                    value={documentsDanyFilter}
                    onChange={(e) => setDocumentsDanyFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Toutes les étoiles</option>
                    <option value="non_renseigne">Non renseigné</option>
                    <option value="1">1 ⭐ - Premier brouillon</option>
                    <option value="2">2 ⭐ - Commencé mais pas prêt</option>
                    <option value="3">3 ⭐ - Peut être en prod</option>
                    <option value="4">4 ⭐ - Satisfait mais manque qqch</option>
                    <option value="5">5 ⭐ - Tout est sur la coche</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">⭐ Spécialiste</label>
                  <select
                    value={documentsSpecialistFilter}
                    onChange={(e) => setDocumentsSpecialistFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="tous">Toutes les étoiles</option>
                    <option value="non_renseigne">Non renseigné</option>
                    <option value="1">1 ⭐ - Premier brouillon</option>
                    <option value="2">2 ⭐ - Commencé mais pas prêt</option>
                    <option value="3">3 ⭐ - Peut être en prod</option>
                    <option value="4">4 ⭐ - Satisfait mais manque qqch</option>
                    <option value="5">5 ⭐ - Tout est sur la coche</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end">
                <span className="text-[11px] text-gray-500 font-medium">
                  {filteredDocuments.length} {filteredDocuments.length === 1 ? 'document' : 'documents'}
                </span>
              </div>
            </div>

            {/* Liste des documents */}
            <div className="space-y-2">
              {documentsLoading && (
                <div className="px-4 py-3 text-[11px] text-gray-500 bg-white rounded-xl border border-gray-200 shadow-sm">
                  Chargement des documents...
                </div>
              )}
              {documentsError && (
                <div className="px-4 py-3 text-[11px] text-red-700 bg-red-50 rounded-xl border border-red-200 shadow-sm">
                  {documentsError}
                </div>
              )}

              {!documentsLoading && !documentsError && filteredDocuments.length === 0 && (
                <div className="px-4 py-6 text-center text-[11px] text-gray-500 bg-white rounded-xl border border-gray-200 shadow-sm">
                  Aucun document généré ne correspond aux filtres actuels.
                </div>
              )}

              {filteredDocuments.map((doc) => {
                const client = clients.find((c) => c.id === doc.client_id);
                const type = DOCUMENT_TYPES.find((t) => t.code === doc.document_type_code);
                const createdAt = doc.created_at
                  ? new Date(doc.created_at).toLocaleDateString('fr-CA')
                  : 'N/A';
                const updatedAt = doc.updated_at ? new Date(doc.updated_at).toLocaleDateString('fr-CA') : 'N/A';

                const statusBadge = doc.archived
                  ? { label: 'Archivé', classes: 'bg-gray-100 text-gray-700 border-gray-200' }
                  : doc.presented_client
                  ? { label: 'Présenté', classes: 'bg-purple-50 text-purple-700 border-purple-200' }
                  : doc.showpriseclient
                  ? { label: 'Prêt pour PRIsME client', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
                  : { label: 'Brouillon interne', classes: 'bg-amber-50 text-amber-700 border-amber-200' };

                return (
                  <div
                    key={doc.id}
                    className="px-4 py-3 text-xs bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        <div className="w-8 h-8 rounded-xl bg-bz-blue/10 flex items-center justify-center text-bz-blue">
                          <FileText className="w-4 h-4" />
                        </div>
                      </div>
                      <div>
                        <p className="mb-1">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusBadge.classes}`}
                          >
                            {statusBadge.label}
                          </span>
                        </p>
                        <p className="text-[11px] font-semibold text-bz-blue uppercase tracking-wide mb-0.5">
                          {doc.document_type_code} • {type?.title || 'Type inconnu'}
                        </p>
                        <h4 className="text-sm font-bold text-gray-900 mb-0.5">
                          {doc.title || 'Document sans titre'}
                        </h4>
                        <p className="text-[11px] text-gray-600">
                          {client?.nom || `Client #${doc.client_id}`} • Créé le {createdAt}
                        </p>
                        <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
                          <div className="flex items-center gap-1.5">
                            <UserAvatar ppId={doc.created_by} title={`Créé par: ${getPPLabel(doc.created_by)}`} />
                            <span>{getPPLabel(doc.created_by)}</span>
                          </div>
                          <span>•</span>
                          <div className="flex items-center gap-1.5">
                            <UserAvatar ppId={doc.last_modified_by} title={`Modifié par: ${getPPLabel(doc.last_modified_by)}`} />
                            <span>{getPPLabel(doc.last_modified_by)} • {updatedAt}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col md:items-end gap-3 text-[11px]">
                      <div className="flex flex-col gap-2 items-start md:items-end">
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] text-gray-500 font-medium">Dany</span>
                            <StarRating rating={doc.approved_dany || undefined} size="sm" />
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] text-gray-500 font-medium">Spécialiste</span>
                            <StarRating rating={doc.approved_specialist || undefined} size="sm" />
                          </div>
                        </div>
                        {doc.comments && (
                          <div className="max-w-xs text-[10px] text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-200">
                            {doc.comments.length > 100 ? `${doc.comments.substring(0, 100)}...` : doc.comments}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                        <button
                          type="button"
                          onClick={() => openDocAuditModal(doc.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-300 text-gray-700 bg-white text-[11px] font-semibold hover:bg-gray-100 transition-colors"
                        >
                          Historique
                        </button>
                        <button
                          type="button"
                          onClick={() => setViewingDocumentId(doc.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-bz-blue/60 text-bz-blue bg-white text-[11px] font-semibold hover:bg-bz-blue hover:text-white transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          Voir / Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingDocumentId(doc.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-emerald-500/60 text-emerald-700 bg-white text-[11px] font-semibold hover:bg-emerald-500 hover:text-white transition-colors"
                        >
                          <Edit2 className="w-3 h-3" />
                          Éditer
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (confirm(`Êtes-vous sûr de vouloir supprimer ce document "${doc.title || 'sans titre'}" ?`)) {
                              try {
                                await deleteDocument(doc.id);
                                // Recharger les documents en une seule requête
                                const allDocs = await getAllDocuments(true);
                                setDocuments(allDocs);
                              } catch (e: any) {
                                alert(`Erreur lors de la suppression: ${e?.message || 'Erreur inconnue'}`);
                              }
                            }
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-500/60 text-red-700 bg-white text-[11px] font-semibold hover:bg-red-500 hover:text-white transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Modal historique document */}
        {docAuditModalId && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Historique — document #{docAuditModalId}</h2>
                  <p className="text-xs text-gray-600">Modifications (versions) avec auteur et date.</p>
                </div>
                <button
                  onClick={() => {
                    setDocAuditModalId(null);
                    setDocAuditHistory(null);
                    setDocAuditError(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <span className="text-2xl text-gray-500">&times;</span>
                </button>
              </div>
              <div className="p-6 space-y-3">
                {docAuditLoading && <div className="text-sm text-gray-600">Chargement…</div>}
                {docAuditError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {docAuditError}
                  </div>
                )}
                {!docAuditLoading && !docAuditError && (
                  <div className="space-y-2">
                    {(docAuditHistory || []).length === 0 ? (
                      <div className="text-sm text-gray-600">Aucune entrée d’historique.</div>
                    ) : (
                      (docAuditHistory || []).map((h, idx) => (
                        <div key={idx} className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <UserAvatar ppId={h.modified_by} title={`Modifié par: ${getPPLabel(h.modified_by)}`} />
                              <div>
                                <div className="text-sm font-semibold text-gray-900">{getPPLabel(h.modified_by)}</div>
                                <div className="text-xs text-gray-600">
                                  Version {h.version} • {formatDateTime(h.modified_at)}
                                </div>
                              </div>
                            </div>
                            {h.commentaire && (
                              <div className="text-xs text-gray-700 max-w-[55%]">
                                <span className="font-semibold">Note:</span> {h.commentaire}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SECTION GABARITS - Formulaires */}
        {activeSection === 'gabarits' && activeGabaritsTab === 'formulaires' && (
          <section className="space-y-4">
            <div className="space-y-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Gabarits de prise de besoin</h3>
                <p className="text-xs text-gray-600">
                  Vue globale de tous les gabarits de prise de besoin PRIsME, tous départements confondus. Plus tard,
                  chaque prise de besoin client sera basée sur l&apos;un de ces gabarits et pourra être sélectionnée
                  dans un écran dédié.
                </p>
              </div>

              {/* Filtres gabarits + boutons actions */}
              <div className="flex flex-col md:flex-row gap-2 text-xs md:w-auto w-full md:items-center md:justify-between">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setPriseDeBesoinInitialTab('rapport');
                      setIsPriseDeBesoinModalOpen(true);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-bz-blue/40 text-bz-blue font-semibold hover:bg-bz-blue hover:text-white transition-colors"
                  >
                    Voir un exemple de rapport de prise de besoin (client fictif)
                  </button>
                  <button
                    onClick={() => setIsExportExcelModalOpen(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-600 to-blue-600 text-white font-semibold hover:from-emerald-700 hover:to-blue-700 transition-colors shadow-md"
                  >
                    <Download className="w-4 h-4" />
                    Exporter gabarit en Excel
                  </button>
                </div>

                <div className="flex flex-col md:flex-row gap-2 text-xs md:w-auto w-full md:items-center md:justify-end">
                  <input
                    type="text"
                    value={intakeSearch}
                    onChange={(e) => setIntakeSearch(e.target.value)}
                    placeholder="Rechercher un gabarit..."
                    className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue max-w-xs"
                  />
                  <select
                    value={intakeTypeFilter}
                    onChange={(e) => setIntakeTypeFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue max-w-[160px]"
                  >
                    <option value="tous">Tous les types</option>
                    {DOCUMENT_TYPES.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.code}
                      </option>
                    ))}
                  </select>
                  <select
                    value={intakeDomainFilter}
                    onChange={(e) => setIntakeDomainFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue max-w-[160px]"
                  >
                    <option value="tous">Tous les domaines</option>
                    {allDomains.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <select
                    value={intakeAvailabilityFilter}
                    onChange={(e) =>
                      setIntakeAvailabilityFilter(e.target.value as 'tous' | 'production' | 'coming_soon' | 'archived')
                    }
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-1 focus:ring-bz-blue focus:border-bz-blue max-w-[180px]"
                  >
                    <option value="tous">Tous les statuts</option>
                    <option value="production">Disponibles en production</option>
                    <option value="coming_soon">À venir</option>
                    <option value="archived">Archivés</option>
                  </select>
                  <span className="text-[11px] text-gray-500 font-medium px-2">
                    {filteredIntakes.length} {filteredIntakes.length === 1 ? 'gabarit' : 'gabarits'}
                  </span>
                </div>
              </div>
            </div>

            {/* Listing gabarits */}
            <div className="bg-transparent rounded-2xl">
              <div className="hidden md:grid [grid-template-columns:minmax(0,2.2fr)_minmax(0,1.8fr)_minmax(0,1.1fr)_minmax(0,3fr)_minmax(0,1.8fr)] gap-2 px-4 py-2 text-[11px] font-semibold text-gray-500">
                <div>Gabarit</div>
                <div>Type de document</div>
                <div>Domaine</div>
                <div>Résumé</div>
              </div>
              <div className="space-y-2">
                {filteredIntakes.map((tpl) => (
                  <div
                    key={`${tpl.documentTypeCode}:${tpl.id}`}
                    className="px-4 py-3 text-xs bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all grid grid-cols-1 md:[grid-template-columns:minmax(0,2.2fr)_minmax(0,1.8fr)_minmax(0,1.1fr)_minmax(0,3fr)_minmax(0,1.8fr)] gap-2 items-start"
                  >
                    <div>
                      <p className="mb-1">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            tpl.status === 'production'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : tpl.status === 'archived'
                              ? 'bg-gray-100 text-gray-700 border border-gray-300'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {tpl.status === 'production' ? 'Production' : tpl.status === 'archived' ? 'Archivé' : 'À venir'}
                        </span>
                      </p>
                      <p className="font-semibold text-gray-900">{tpl.name}</p>
                      <p className="md:hidden text-[11px] text-gray-500 mt-0.5">
                        {tpl.documentTypeCode} • {tpl.domain}
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <TemplateLinkedDocumentCell templateId={tpl.id} mode="type" />
                    </div>
                    <div className="hidden md:flex items-center">
                      <TemplateLinkedDocumentCell templateId={tpl.id} mode="domain" />
                    </div>
                    <div className="text-[11px] text-gray-600">{tpl.description}</div>
                    <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTemplateId(tpl.id);
                          // Déterminer la version du template
                          const template = INTAKE_TEMPLATES.find((tmpl) => tmpl.id === tpl.id);
                          setSelectedTemplateVersion(template?.version || '1.0.0');
                          setSelectedClient2026Schema(template?.version === '3.1.0' ? 3 : template?.version === '3.0.0' ? 2 : undefined);
                          
                          // Ouvrir la modal appropriée selon le template
                          if (tpl.id === 'politique-cybersecurite-v2') {
                            setIsPriseDeBesoinPolitiqueCybersecuriteOpen(true);
                          } else if (tpl.id === 'plan-directeur-technique-v1') {
                            setIsPriseDeBesoinPlanDirecteurTechniqueOpen(true);
                          } else if (tpl.id === 'plan-budgetaire-5ans-v1') {
                            setIsPriseDeBesoinPlanBudgetaire5AnsOpen(true);
                          } else {
                            // Pour les autres templates (pdti-standard-pme, etc.)
                            setIsPriseDeBesoinWizardOpen(true);
                          }
                        }}
                        className="px-3 py-1 rounded-full border border-bz-blue/60 text-bz-blue bg-white text-[11px] font-semibold hover:bg-bz-blue hover:text-white transition-colors"
                      >
                        Créer le formulaire
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTemplateId(tpl.id);
                          setPriseDeBesoinInitialTab('rapport');
                          setIsPriseDeBesoinModalOpen(true);
                        }}
                        className="px-3 py-1 rounded-full border border-emerald-500/60 text-emerald-700 bg-white text-[11px] font-semibold hover:bg-emerald-500 hover:text-white transition-colors"
                      >
                        Voir rapport mock
                      </button>
                      <button
                        type="button"
                        onClick={() => setLinkModalTemplate({ id: tpl.id, name: tpl.name })}
                        className="px-3 py-1 rounded-full border border-gray-300 text-gray-700 bg-white text-[11px] font-semibold hover:bg-gray-100 transition-colors"
                      >
                        Gérer les types liés
                      </button>
                    </div>
                  </div>
                ))}

                {filteredIntakes.length === 0 && (
                  <div className="px-4 py-6 text-center text-[11px] text-gray-500">
                    Aucun gabarit ne correspond aux filtres actuels.
                  </div>
                )}
              </div>
            </div>

            <p className="text-[11px] text-gray-500">
              Prochaine étape: brancher ces gabarits sur une table en base de données et sur le module de prises de
              besoin clients, pour ensuite permettre la sélection d&apos;une prise de besoin existante au moment de la
              génération des documents.
            </p>
          </section>
        )}

        {/* SECTION 3 – Prises de besoin (BD) */}
        {/* SECTION LIVRABLES CRÉÉS - Formulaires */}
        {activeSection === 'livrables' && activeLivrablesTab === 'formulaires' && (
          <section className="space-y-4">
            <div className="space-y-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Prises de besoin enregistrées</h3>
                <p className="text-xs text-gray-600">
                  Liste de toutes les prises de besoin créées et enregistrées dans la base de données. Vous pouvez les
                  filtrer, les consulter et les éditer.
                </p>
              </div>
            </div>

            {/* Filtres */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Recherche</label>
                  <input
                    type="text"
                    value={besoinSearch}
                    onChange={(e) => setBesoinSearch(e.target.value)}
                    placeholder="Nom, description..."
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Client</label>
                  <select
                    value={besoinClientFilter}
                    onChange={(e) => setBesoinClientFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="tous">Tous les clients</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Visibilité client</label>
                  <select
                    value={besoinVisibilityFilter}
                    onChange={(e) => setBesoinVisibilityFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="tous">Toutes les prises de besoin</option>
                    <option value="visible">Seulement visibles pour le client</option>
                    <option value="cache">Cachées côté client</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1">Gabarit</label>
                  <select
                    value={besoinTemplateFilter}
                    onChange={(e) => setBesoinTemplateFilter(e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="tous">Tous les gabarits</option>
                    <option value="archives">Formulaires archivés</option>
                    {Object.keys(INTAKE_TEMPLATES_BY_TYPE).map((code) => {
                      const type = DOCUMENT_TYPES.find((t) => t.code === code);
                      return (
                        <option key={code} value={code}>
                          {type?.title || code}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end mt-3">
                <span className="text-[11px] text-gray-500 font-medium">
                  {filteredBesoinsCount} {filteredBesoinsCount === 1 ? 'prise de besoin' : 'prises de besoin'}
                </span>
              </div>
            </div>

            {/* Liste des prises de besoin */}
            <BesoinListSection
              search={besoinSearch}
              clientFilter={besoinClientFilter}
              visibilityFilter={besoinVisibilityFilter}
              templateFilter={besoinTemplateFilter}
              clients={clients}
              partiesPrenantes={partiesPrenantes}
              allowedDomains={isCyberOnlyUser ? ['Cybersécurité'] : undefined}
              onEdit={async (besoinId) => {
                setEditingBesoinId(besoinId);
                // Charger le besoin pour obtenir son template_id
                try {
                  const { getBesoinById } = await import('../services/besoinsService');
                  const besoin = await getBesoinById(besoinId);
                  if (besoin) {
                    setSelectedTemplateId(besoin.template_id || 'pdti-standard-pme');
                    setSelectedTemplateVersion(besoin.template_version || '1.0.0');
                    setSelectedClient2026Schema(Number((besoin as any)?.metadata?.client2026_schema) || undefined);
                    
                    // Ouvrir la modal appropriée selon le template
                    if (besoin.template_id === 'politique-cybersecurite-v2') {
                      setIsPriseDeBesoinPolitiqueCybersecuriteOpen(true);
                    } else if (besoin.template_id === 'plan-directeur-technique-v1') {
                      setIsPriseDeBesoinPlanDirecteurTechniqueOpen(true);
                    } else if (besoin.template_id === 'plan-budgetaire-5ans-v1') {
                      setIsPriseDeBesoinPlanBudgetaire5AnsOpen(true);
                    } else {
                      // Pour pdti-standard-pme et autres templates
                      setIsPriseDeBesoinWizardOpen(true);
                    }
                  } else {
                    // Fallback si le besoin n'est pas trouvé
                    setIsPriseDeBesoinWizardOpen(true);
                  }
                } catch (error) {
                  console.error('Erreur lors du chargement du besoin:', error);
                  // Fallback en cas d'erreur
                  setIsPriseDeBesoinWizardOpen(true);
                }
              }}
            />
          </section>
        )}
      </div>
      <PlanDirecteurModal
        isOpen={isPlanDirecteurModalOpen}
        onClose={() => setIsPlanDirecteurModalOpen(false)}
        initialTab={planDirecteurInitialTab}
      />
      <PlanDirecteurTechniqueModal
        isOpen={isPlanDirecteurTechniqueModalOpen}
        onClose={() => setIsPlanDirecteurTechniqueModalOpen(false)}
      />
      <Roadmap24Modal
        isOpen={isRoadmap24ModalOpen}
        onClose={() => setIsRoadmap24ModalOpen(false)}
      />
      <ObjectifsKPIModal
        isOpen={isObjectifsKPIModalOpen}
        onClose={() => setIsObjectifsKPIModalOpen(false)}
      />
      <CahierChargeModal
        isOpen={isCahierChargeModalOpen}
        onClose={() => setIsCahierChargeModalOpen(false)}
      />
      <MatriceRACIModal
        isOpen={isMatriceRACIModalOpen}
        onClose={() => setIsMatriceRACIModalOpen(false)}
      />
      <PlanBudgetaireModal
        isOpen={isPlanBudgetaireModalOpen}
        onClose={() => setIsPlanBudgetaireModalOpen(false)}
      />
      <PlanBudgetaire5AnsModal
        isOpen={isPlanBudgetaire5AnsModalOpen}
        onClose={() => setIsPlanBudgetaire5AnsModalOpen(false)}
      />
      <RegistreAmeliorationModal
        isOpen={isRegistreAmeliorationModalOpen}
        onClose={() => setIsRegistreAmeliorationModalOpen(false)}
      />
      <RegistreRisquesModal
        isOpen={isRegistreRisquesModalOpen}
        onClose={() => setIsRegistreRisquesModalOpen(false)}
      />
      <OffreServiceBZModal
        isOpen={isOffreServiceBZModalOpen}
        onClose={() => setIsOffreServiceBZModalOpen(false)}
      />
      <PolitiqueCybersecuriteModal
        isOpen={isPolitiqueCybersecuriteModalOpen}
        onClose={() => setIsPolitiqueCybersecuriteModalOpen(false)}
      />
      <RapportIncidentModal
        isOpen={isRapportIncidentModalOpen}
        onClose={() => setIsRapportIncidentModalOpen(false)}
      />
      <PolitiqueIAModal
        isOpen={isPolitiqueIAModalOpen}
        onClose={() => setIsPolitiqueIAModalOpen(false)}
      />
      <PolitiqueGestionIncidentsModal
        isOpen={isPolitiqueGestionIncidentsModalOpen}
        onClose={() => setIsPolitiqueGestionIncidentsModalOpen(false)}
      />
      <PolitiqueSecuriteInformationModal
        isOpen={isPolitiqueSecuriteInformationModalOpen}
        onClose={() => setIsPolitiqueSecuriteInformationModalOpen(false)}
      />
      <PolitiqueClassificationIncidentsModal
        isOpen={isPolitiqueClassificationIncidentsModalOpen}
        onClose={() => setIsPolitiqueClassificationIncidentsModalOpen(false)}
      />
      <PolitiqueEscaladeIncidentsModal
        isOpen={isPolitiqueEscaladeIncidentsModalOpen}
        onClose={() => setIsPolitiqueEscaladeIncidentsModalOpen(false)}
      />
      <PlanInterventionIncidentModal
        isOpen={isPlanInterventionIncidentModalOpen}
        onClose={() => setIsPlanInterventionIncidentModalOpen(false)}
      />
      <PlanIncidentMajeurModal
        isOpen={isPlanIncidentMajeurModalOpen}
        onClose={() => setIsPlanIncidentMajeurModalOpen(false)}
      />
      <PlanCommunicationIncidentModal
        isOpen={isPlanCommunicationIncidentModalOpen}
        onClose={() => setIsPlanCommunicationIncidentModalOpen(false)}
      />
      <PlanContinuiteTiModal
        isOpen={isPlanContinuiteTiModalOpen}
        onClose={() => setIsPlanContinuiteTiModalOpen(false)}
      />
      <PlanIncidentViePriveeModal
        isOpen={isPlanIncidentViePriveeModalOpen}
        onClose={() => setIsPlanIncidentViePriveeModalOpen(false)}
      />
      <ProcedureGestionIncidentsModal
        isOpen={isProcedureGestionIncidentsModalOpen}
        onClose={() => setIsProcedureGestionIncidentsModalOpen(false)}
      />
      <ProcedureReponseIncidentsSecuriteModal
        isOpen={isProcedureReponseIncidentsSecuriteModalOpen}
        onClose={() => setIsProcedureReponseIncidentsSecuriteModalOpen(false)}
      />
      <ProcedureEscaladeModal
        isOpen={isProcedureEscaladeModalOpen}
        onClose={() => setIsProcedureEscaladeModalOpen(false)}
      />
      <ProcedureGestionPreuvesModal
        isOpen={isProcedureGestionPreuvesModalOpen}
        onClose={() => setIsProcedureGestionPreuvesModalOpen(false)}
      />
      <PostIncidentReviewModal
        isOpen={isPostIncidentReviewModalOpen}
        onClose={() => setIsPostIncidentReviewModalOpen(false)}
      />
      <AfterActionReportModal
        isOpen={isAfterActionReportModalOpen}
        onClose={() => setIsAfterActionReportModalOpen(false)}
      />
      <RapportLeconsApprisesModal
        isOpen={isRapportLeconsApprisesModalOpen}
        onClose={() => setIsRapportLeconsApprisesModalOpen(false)}
      />
      <PriseDeBesoinModal
        isOpen={isPriseDeBesoinModalOpen}
        onClose={() => setIsPriseDeBesoinModalOpen(false)}
        initialTab={priseDeBesoinInitialTab}
        templateId={selectedTemplateId}
      />
      <PriseDeBesoinWizardModal
        isOpen={isPriseDeBesoinWizardOpen}
        onClose={() => {
          setIsPriseDeBesoinWizardOpen(false);
          setEditingBesoinId(undefined);
        }}
        besoinId={editingBesoinId}
        templateId={selectedTemplateId}
        templateVersion={selectedTemplateVersion}
        client2026Schema={selectedClient2026Schema}
        onSuccess={(besoinId) => {
          console.log('✅ Prise de besoin sauvegardée avec succès:', besoinId);
          setEditingBesoinId(undefined);
        }}
      />
      <PriseDeBesoinPlanDirecteurTechniqueModal
        isOpen={isPriseDeBesoinPlanDirecteurTechniqueOpen}
        onClose={() => {
          setIsPriseDeBesoinPlanDirecteurTechniqueOpen(false);
          setEditingBesoinId(undefined);
        }}
        besoinId={editingBesoinId}
        templateId={selectedTemplateId}
        templateVersion={selectedTemplateVersion}
        onSuccess={(besoinId) => {
          console.log('✅ Prise de besoin Plan Directeur Technique sauvegardée avec succès:', besoinId);
          setEditingBesoinId(undefined);
          // Rafraîchir la liste des besoins si nécessaire
        }}
      />
      <PriseDeBesoinPolitiqueCybersecuriteModal
        isOpen={isPriseDeBesoinPolitiqueCybersecuriteOpen}
        onClose={() => {
          setIsPriseDeBesoinPolitiqueCybersecuriteOpen(false);
          setEditingBesoinId(undefined);
        }}
        besoinId={editingBesoinId}
        templateId={selectedTemplateId}
        templateVersion={selectedTemplateVersion}
        onSuccess={(besoinId) => {
          console.log('✅ Prise de besoin Politique de cybersécurité sauvegardée avec succès:', besoinId);
          setEditingBesoinId(undefined);
        }}
      />
      <PriseDeBesoinPlanBudgetaire5AnsModal
        isOpen={isPriseDeBesoinPlanBudgetaire5AnsOpen}
        onClose={() => {
          setIsPriseDeBesoinPlanBudgetaire5AnsOpen(false);
          setEditingBesoinId(undefined);
        }}
        besoinId={editingBesoinId}
        templateId={selectedTemplateId}
        templateVersion={selectedTemplateVersion}
        onSuccess={() => {
          setEditingBesoinId(undefined);
        }}
      />
      <ExportTemplateExcelModal
        isOpen={isExportExcelModalOpen}
        onClose={() => setIsExportExcelModalOpen(false)}
      />
      {linkModalTemplate && (
        <TemplateDocumentLinksModal
          isOpen={!!linkModalTemplate}
          onClose={() => setLinkModalTemplate(null)}
          templateId={linkModalTemplate.id}
          templateName={linkModalTemplate.name}
        />
      )}
      {documentWizardType && (
        <DocumentCreationWizardModal
          isOpen={!!documentWizardType}
          onClose={() => setDocumentWizardType(null)}
          documentTypeCode={documentWizardType}
          onCreated={(docId) => setViewingDocumentId(docId)}
        />
      )}
      {viewingDocumentId && (
        <ManagedDocumentViewerModal
          isOpen={!!viewingDocumentId}
          documentId={viewingDocumentId}
          onClose={() => setViewingDocumentId(null)}
        />
      )}
      {editingDocumentId && (
        <DocumentEditModal
          isOpen={!!editingDocumentId}
          onClose={() => setEditingDocumentId(null)}
          document={documents.find((d) => d.id === editingDocumentId) || null}
          onSaved={async () => {
            // Recharger les documents en une seule requête
            const allDocs = await getAllDocuments(true);
            setDocuments(allDocs);
            setEditingDocumentId(null);
          }}
        />
      )}
    </div>
  );
}
